import type { Diagram, DomainObject, Relationship, ModelDocument, Operation, Connector, Point } from '../../shared/contracts.js';
import { boxesFromAppearances, connectorUpdatesForMovedBoxes, type CanvasBox } from './canvasGeometry.js';
import { resolveEntityPlacement } from './canvasPlacement.js';

/** Select the active diagram's complete content, independently of its viewport. */
export function diagramSelectionIds(diagram: Diagram | undefined, objects: readonly DomainObject[], relationships: readonly Relationship[]): string[] {
  if (!diagram) return [];
  const known = new Set(objects.filter(object => object.representationId === diagram.representationId).map(object => object.objectId));
  const entities = new Set(diagram.appearances.map(appearance => appearance.objectId).filter(id => known.has(id)));
  const edges = relationships.filter(relationship => relationship.representationId === diagram.representationId && entities.has(relationship.sourceObjectId) && entities.has(relationship.targetObjectId));
  return [...entities, ...edges.map(relationship => relationship.relationshipId)];
}

/** Canvas shortcuts leave native editing and embedded rich-text editors alone. */
export function isTextEditingTarget(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"]),[role="textbox"],[role="combobox"]');
}

/** A pointer gesture publishes drafts until its single successful release.
 * Escape/cancel, foreign pointers and stationary clicks cannot become commands.
 */
export function createRouteDragSession(pointerId: number, start: Point, zoom: number, update: (point: Point, delta: Point, complete: boolean) => void, canceled: () => void) {
  let moved = false, finished = false;
  const advance = (id: number, point: Point, complete: boolean) => {
    if (id !== pointerId || finished) return false;
    const delta = { x: point.x - start.x, y: point.y - start.y };
    moved ||= Math.hypot(delta.x, delta.y) * zoom >= 2;
    if (complete) finished = true;
    if (moved) update(point, delta, complete);
    return true;
  };
  return {
    move: (id: number, point: Point) => advance(id, point, false),
    finish: (id: number, point: Point) => advance(id, point, true),
    cancel: () => { if (!finished) { finished = true; canceled(); } },
  };
}

/** Route adaptation is part of the same undo transaction as the user's geometry edit.
 * It never runs just because a model or user preference was loaded. Explicit connector
 * changes (layout restore, for example) take precedence over this ordinary-movement policy.
 */
export function withMovedConnectorUpdates(document: ModelDocument, operations: Operation[], preserveManualRoutes: boolean, measured: Readonly<Record<string, readonly CanvasBox[]>> = {}, adaptMovement = true, placement?: { enabled: boolean; pushNeighbors: boolean }): Operation[] {
  if (!adaptMovement) return operations;
  const result = [...operations];
  for (const diagram of document.diagrams) {
    const edits = operations.filter(operation => operation.type === 'appearance.update' && operation.diagramId === diagram.diagramId);
    if (!edits.length) continue;
    const before = [...(measured[diagram.diagramId] ?? boxesFromAppearances(diagram.appearances))];
    let after = before.map(box => {
      let next = { ...box };
      for (const operation of edits) {
        if (operation.type !== 'appearance.update' || operation.appearanceId !== box.id) continue;
        const changes = operation.changes as { position?: { x: number; y: number }; width?: number; height?: number };
        next = { ...next, ...changes.position, ...(changes.width === undefined ? {} : { width: changes.width }), ...(changes.height === undefined ? {} : { height: changes.height }) };
      }
      return next;
    });
    if (placement?.enabled) {
      const editedIds = edits.flatMap(operation => operation.type === 'appearance.update' && ['position','width','height'].some(key => Object.hasOwn(operation.changes as object,key)) ? [operation.appearanceId as string] : []);
      const resolved = resolveEntityPlacement(before,after,editedIds,placement.pushNeighbors);
      for(const box of resolved) {
        const proposed = after.find(value=>value.id===box.id)!;
        if(proposed.x!==box.x || proposed.y!==box.y) result.push({type:'appearance.update',diagramId:diagram.diagramId,appearanceId:box.id,changes:{position:{x:box.x,y:box.y}}});
      }
      after=resolved;
    }
    const explicit = new Set(operations.flatMap(operation => operation.diagramId !== diagram.diagramId ? [] : operation.type === 'connector.set' ? [(operation.connector as Connector).relationshipId] : operation.type === 'connector.remove' ? [operation.relationshipId as string] : []));
    const relationships = document.relationships.filter(relationship => relationship.representationId === diagram.representationId);
    for (const update of connectorUpdatesForMovedBoxes(before, after, relationships, diagram.connectors, preserveManualRoutes)) {
      if (explicit.has(update.relationshipId)) continue;
      const current = diagram.connectors.find(connector => connector.relationshipId === update.relationshipId) ?? { relationshipId: update.relationshipId };
      result.push({ type: 'connector.set', diagramId: diagram.diagramId, connector: { ...current, ...update.changes } });
    }
  }
  return result;
}

export const EDGE_DRAG = { zone: 40, dwellMs: 500, panPixelsPerSecond: 90, burstMs: 1000, pauseMs: 400, minZoom: .2, zoomPerSecond: .08, expansion: 2400 } as const;
export type DragViewport = Point & { zoom:number };
export type DragBounds = {left:number;top:number;right:number;bottom:number};
/** Existing large/imported diagrams remain editable; bounded dragging can extend
 * their initial extent by 2400 diagram pixels in each direction per gesture. */
export function dragBounds(boxes:readonly CanvasBox[],unlimited=false):DragBounds {
  if(unlimited)return {left:-Infinity,top:-Infinity,right:Infinity,bottom:Infinity};
  return {left:Math.min(0,...boxes.map(b=>b.x))-EDGE_DRAG.expansion,top:Math.min(0,...boxes.map(b=>b.y))-EDGE_DRAG.expansion,
    right:Math.max(0,...boxes.map(b=>b.x+b.width))+EDGE_DRAG.expansion,bottom:Math.max(0,...boxes.map(b=>b.y+b.height))+EDGE_DRAG.expansion};
}
export function clampDragPosition(position:Point,size:{width:number;height:number},bounds:DragBounds):Point {
  return {x:Math.max(bounds.left,Math.min(bounds.right-size.width,position.x)),y:Math.max(bounds.top,Math.min(bounds.bottom-size.height,position.y))};
}
/** Time-based motion is independent of frame rate and interface scale. Leaving an
 * edge resets dwell; each one-second burst requires a deliberate continued hold. */
export function createEdgeDragController() {
  let edge='',entered=0,last=0;
  return {reset(){edge='';entered=0;last=0;},step(now:number,pointer:Point,size:{width:number;height:number},viewport:DragViewport,bounds:DragBounds):DragViewport {
    const dx=pointer.x<EDGE_DRAG.zone?-1:pointer.x>size.width-EDGE_DRAG.zone?1:0;
    const dy=pointer.y<EDGE_DRAG.zone?-1:pointer.y>size.height-EDGE_DRAG.zone?1:0;
    const nextEdge=`${dx},${dy}`;
    if(!dx&&!dy){edge='';last=now;return viewport;}
    if(nextEdge!==edge){edge=nextEdge;entered=now;last=now;return viewport;}
    const dt=Math.min(50,Math.max(0,now-last))/1000;last=now;
    const elapsed=now-entered-EDGE_DRAG.dwellMs;
    if(elapsed<0||elapsed%(EDGE_DRAG.burstMs+EDGE_DRAG.pauseMs)>=EDGE_DRAG.burstMs)return viewport;
    const flowX=(pointer.x-viewport.x)/viewport.zoom,flowY=(pointer.y-viewport.y)/viewport.zoom;
    if((dx<0&&flowX<=bounds.left+20)||(dx>0&&flowX>=bounds.right-20)||(dy<0&&flowY<=bounds.top+20)||(dy>0&&flowY>=bounds.bottom-20))return viewport;
    const zoom=Math.max(Math.min(viewport.zoom,EDGE_DRAG.minZoom),viewport.zoom*(1-EDGE_DRAG.zoomPerSecond*dt));
    return {zoom,x:pointer.x-flowX*zoom-dx*EDGE_DRAG.panPixelsPerSecond*dt,y:pointer.y-flowY*zoom-dy*EDGE_DRAG.panPixelsPerSecond*dt};
  }};
}
